import React from 'react';
import { BookOpen, ExternalLink, Power, PowerOff, Trash2, Globe, Lock } from 'lucide-react';
import type { StrategyListItem } from '../../types/strategy';

interface StrategyLibraryCardProps {
  strategy: StrategyListItem;
  isOwner: boolean;
  onView: (id: number) => void;
  onToggleActive: (id: number, isActive: boolean) => void;
  onToggleUserActivation?: (id: number, isActive: boolean) => void;
  onDelete: (id: number) => void;
}

const StrategyLibraryCard: React.FC<StrategyLibraryCardProps> = ({
  strategy,
  isOwner,
  onView,
  onToggleActive,
  onToggleUserActivation,
  onDelete,
}) => {
  const statusColor: Record<string, string> = {
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    approved_with_notes: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
    rejected: 'bg-red-500/10 text-red-400 border-red-500/30',
  };

  const extractionLabel: Record<string, string> = {
    pending: 'Queued',
    downloading: 'Downloading...',
    transcribing: 'Transcribing...',
    structuring: 'Structuring...',
    completed: 'Extracted',
    failed: 'Failed',
  };

  // For community strategies, use user_is_active; for owned, use is_active
  const isCommunity = strategy.type === 'community';
  const effectiveActive = isCommunity
    ? (strategy.user_is_active ?? true)
    : strategy.is_active;

  return (
    <div
      className="group border border-slate-700 hover:border-slate-600 rounded-xl bg-slate-800/60 p-4 transition cursor-pointer"
      onClick={() => onView(strategy.id)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen size={14} className="text-purple-400 shrink-0" />
          <h3 className="text-sm font-semibold text-white truncate">{strategy.name}</h3>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {effectiveActive && (
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" title="Active" />
          )}
          {isCommunity ? (
            <Globe size={12} className="text-blue-400" aria-label="Community" />
          ) : (
            <Lock size={12} className="text-slate-500" aria-label="Private" />
          )}
        </div>
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusColor[strategy.status] || 'bg-slate-700 text-slate-400 border-slate-600'}`}>
          {strategy.status}
        </span>
        {strategy.extraction_status && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
            {extractionLabel[strategy.extraction_status] || strategy.extraction_status}
          </span>
        )}
        {strategy.validation_score !== null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
            Score: {strategy.validation_score}
          </span>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-[11px] text-slate-400 mb-3">
        {strategy.win_rate !== null && (
          <span>WR: {strategy.win_rate?.toFixed(1)}%</span>
        )}
        {strategy.total_trades > 0 && (
          <span>{strategy.total_trades} trades</span>
        )}
        {strategy.source_url && (
          <a
            href={strategy.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            Source <ExternalLink size={9} />
          </a>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
        {isOwner ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive(strategy.id, !strategy.is_active);
              }}
              className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition ${
                strategy.is_active
                  ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                  : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
              }`}
              title={strategy.is_active ? 'Deactivate' : 'Activate'}
            >
              {strategy.is_active ? <PowerOff size={10} /> : <Power size={10} />}
              {strategy.is_active ? 'Off' : 'On'}
            </button>
            {!strategy.is_system && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(strategy.id);
                }}
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-red-500/20 text-red-400 hover:bg-red-500/10 transition"
                title="Delete"
              >
                <Trash2 size={10} />
              </button>
            )}
          </>
        ) : isCommunity && onToggleUserActivation ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleUserActivation(strategy.id, !effectiveActive);
            }}
            className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition ${
              effectiveActive
                ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
            }`}
            title={effectiveActive ? 'Deactivate for Me' : 'Activate for Me'}
          >
            {effectiveActive ? <PowerOff size={10} /> : <Power size={10} />}
            {effectiveActive ? 'Deactivate' : 'Activate'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default StrategyLibraryCard;
